import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { onAuthStateChanged, signOut } from 'firebase/auth' 
import { doc, onSnapshot } from 'firebase/firestore'
import { auth, db } from "../../Firebaseconfig"
import logo from "../../Assets/nshpejt.png"
import "./Navbar.css"
import { FaBell, FaBars, FaTimes } from "react-icons/fa";
import { HiSun, HiMoon } from "react-icons/hi";
import { useTranslation } from 'react-i18next';
import NotificationSidebar from './NotificationSidebar';

function Navbar() {
  const { t, i18n } = useTranslation();

  const [user, setUser] = useState(null);
  const [profilePhotoURL, setProfilePhotoURL] = useState('');
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true; // default dark
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 900) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let unsubscribeUserDoc = null;

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (unsubscribeUserDoc) {
        unsubscribeUserDoc();
        unsubscribeUserDoc = null;
      }

      setUser(currentUser);
      setProfilePhotoURL(currentUser?.photoURL || '');
      setLoading(false); // Auth state has been determined

      if (currentUser) {
        unsubscribeUserDoc = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
          const savedPhotoURL = snap.exists() ? snap.data().photoURL : '';
          setProfilePhotoURL(savedPhotoURL || currentUser.photoURL || '');
        }, (error) => {
          console.error("Error listening to user profile:", error);
        });
      }
    });

    return () => {
      unsubscribe();
      if (unsubscribeUserDoc) unsubscribeUserDoc();
    };
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const displayPhotoURL = profilePhotoURL || user?.photoURL;
  

  return (
    <header className="navbar-header">
      <div className="navbar-container">
        <div className="able">
          <Link to="/" className="logo-link" onClick={() => setIsMenuOpen(false)}>
            <img className="logo" src={logo} alt="Logo" />
          </Link>
          <div className="navbar-controls">
            <button
              className="navbar-icon-btn lang-btn"
              onClick={() => {
                const next = i18n.language === 'en' ? 'sq' : 'en';
                i18n.changeLanguage(next);
                localStorage.setItem('i18nextLng', next);
              }}
              title={i18n.language === 'en' ? 'Switch to Albanian' : 'Switch to English'}
            >
              <span className="lang-flag">{i18n.language === 'en' ? '🇦🇱' : '🇬🇧'}</span>
              <span className="lang-code">{i18n.language === 'en' ? 'SQ' : 'EN'}</span>
            </button>
            <button
              className="navbar-icon-btn theme-btn"
              onClick={() => setIsDark(d => !d)}
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? <HiSun className="theme-icon sun" /> : <HiMoon className="theme-icon moon" />}
            </button>
          </div>
        </div>

        <div className="menu">
          <div className={`nav-links ${isMenuOpen ? 'open' : ''}`}>
            <Link to="/" onClick={() => setIsMenuOpen(false)}><h4>{t('navbar.home')}</h4></Link>
            <Link to="/about" onClick={() => setIsMenuOpen(false)}><h4>{t('navbar.about')}</h4></Link>
            <Link to="/products" onClick={() => setIsMenuOpen(false)}><h4>{t('navbar.orders')}</h4></Link>
            <Link to="/contact" onClick={() => setIsMenuOpen(false)}><h4>{t('navbar.contact')}</h4></Link>
            <Link to="/blog" onClick={() => setIsMenuOpen(false)}><h4>{t('navbar.blog')}</h4></Link>
          </div>
          <div className="hamburger" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </div>
        </div>

        <div className="user">
          {loading ? (
            <span>{t('navbar.loading')}</span>
          ) : user ? (
            <>
              <button 
                onClick={() => setIsSidebarOpen(true)} 
                className="notification-btn" 
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', position: 'relative' }}
              >
                <h4><FaBell /></h4>
                {unreadCount > 0 && (
                  <span className="unread-badge">
                    {unreadCount}
                  </span>
                )}
              </button>
              {displayPhotoURL ? (
                <Link to="/dashboard" onClick={() => setIsMenuOpen(false)}>
                  <img 
                    className="user-pfp" 
                    src={displayPhotoURL} 
                    alt={user.displayName || t('navbar.userProfile')} 
                  />
                </Link>
              ) : (
                <span className="user-name">
                  <Link to="/dashboard" onClick={() => setIsMenuOpen(false)}>
                    {user.displayName || user.email || t('navbar.user')}
                  </Link>
                </span>
              )}
              <h4 onClick={handleLogout} className="logout-btn">{t('navbar.logout')}</h4>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setIsMenuOpen(false)}><h4>{t('navbar.login')}</h4></Link>
              <Link to="/signup" onClick={() => setIsMenuOpen(false)}><h4>{t('navbar.signup')}</h4></Link>
            </>
          )}
        </div>
      </div>

      {isMenuOpen && <div className="menu-backdrop" onClick={() => setIsMenuOpen(false)} />}

      <NotificationSidebar 
        isOpen={isSidebarOpen} 
        onClose={() => setIsSidebarOpen(false)} 
        user={user} 
        onUnreadCountChange={setUnreadCount} 
      />
    </header>
  )
}

export default Navbar
